import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  fetchAdminNotificationConfig,
  fetchAdminNotificationLogs,
  fetchAdminNotificationSchedules,
  saveAdminNotificationConfig,
  triggerAdminNotificationRun,
} from "@/lib/sacred-alerts/sacred-alerts-api";
import { Loader2, Play, RefreshCw } from "lucide-react";

export function NotificationsAdmin() {
  const [logs, setLogs] = useState([]);
  const [schedules, setSchedules] = useState({ user_count: 0, schedules: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");
  const [config, setConfig] = useState({ daily_time_utc: "06:00", channels: ["inapp", "email"] });
  const [savingConfig, setSavingConfig] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        fetchAdminNotificationLogs(),
        fetchAdminNotificationSchedules(),
      ]);
      const cfg = await fetchAdminNotificationConfig().catch(() => null);
      setLogs(l);
      setSchedules(s);
      if (cfg) setConfig(cfg);
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Load failed — admin login required");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const runNow = async () => {
    setRunning(true);
    setMessage("");
    try {
      const r = await triggerAdminNotificationRun();
      setMessage(`Run complete: ${r.sent} sent, ${r.skipped} skipped, ${r.users_processed} users`);
      load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Run failed");
    }
    setRunning(false);
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-6" data-testid="notifications-admin">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily reminder schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <label className="text-xs text-muted-foreground">Reminder time (UTC)</label>
            <input
              type="time"
              value={config.daily_time_utc || "06:00"}
              onChange={(e) => setConfig((c) => ({ ...c, daily_time_utc: e.target.value }))}
              className="mt-1 h-9 rounded border border-input bg-background px-3"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              ["inapp", "App popup"],
              ["email", "Email"],
              ["whatsapp", "WhatsApp"],
              ["push", "Push"],
            ].map(([id, label]) => (
              <label key={id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={(config.channels || []).includes(id)}
                  onChange={(e) => {
                    setConfig((c) => {
                      const set = new Set(c.channels || []);
                      if (e.target.checked) set.add(id);
                      else set.delete(id);
                      return { ...c, channels: [...set] };
                    });
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          <Button
            size="sm"
            onClick={async () => {
              setSavingConfig(true);
              setMessage("");
              try {
                const saved = await saveAdminNotificationConfig(config);
                if (saved) setConfig(saved);
                setMessage("Reminder configuration saved.");
              } catch (e) {
                setMessage(e?.response?.data?.detail || "Could not save reminder config.");
              } finally {
                setSavingConfig(false);
              }
            }}
            disabled={savingConfig}
          >
            {savingConfig ? "Saving..." : "Save reminder config"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Notification engine</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button size="sm" onClick={runNow} disabled={running}>
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
              Run daily alerts
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Cron: every 15 minutes. Automatic dispatch happens only at configured UTC reminder time.
          Manual run dispatches immediately for testing.
          {message && <p className="mt-2 text-foreground">{message}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Schedules ({schedules.user_count} users indexed)</CardTitle>
        </CardHeader>
        <CardContent className="max-h-64 overflow-y-auto text-xs font-mono space-y-3">
          {(schedules.schedules || []).length === 0 && <p className="text-muted-foreground">No schedules yet.</p>}
          {(schedules.schedules || []).map((row) => (
            <div key={row.user_id}>
              <p className="font-semibold">{row.user_id}</p>
              {row.items.map((it) => (
                <p key={it.id} className="pl-2 text-muted-foreground">
                  {it.trigger_date} · {it.kind} · {it.title}
                </p>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Run logs</CardTitle>
        </CardHeader>
        <CardContent className="max-h-72 overflow-y-auto text-xs space-y-2">
          {logs.length === 0 && <p className="text-muted-foreground">No cron runs logged yet.</p>}
          {logs.map((log) => (
            <div key={log.id} className="border-b pb-2">
              <p>
                <strong>{log.date}</strong> · {new Date(log.ran_at).toLocaleString()}
              </p>
              <p>
                sent {log.sent} · skipped {log.skipped} · users {log.users_processed}
              </p>
              {log.errors?.length > 0 && (
                <p className="text-destructive">{log.errors.join("; ")}</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
